/**
 * PrivateAppProvider - Authenticated-Only Services and Providers
 *
 * This component wraps all services, state, and infrastructure that should ONLY
 * run for authenticated users. It is conditionally mounted in AppWrapper when user exists.
 *
 * Architecture:
 * - Outer component: Mounts FeaturebaseProvider → SyncConnectionProvider with user.id
 * - Inner component: Uses useSyncConnection() and manages all authenticated services
 * - Initializes BackgroundSyncService
 * - Subscribes to global sync settings and connection events
 * - Manages reconnection modal
 * - Manages dev console
 * - Exposes dev testing utilities
 *
 * Why separated from AppWrapper:
 * - Performance: Public pages (login, signup) don't execute auth-only effects
 * - Security: Auth services don't expose to unauthenticated users
 * - Stability: No remounting of UI framework (HeroUIProvider, ToastProvider) during auth transitions
 *
 * Provider hierarchy: FeaturebaseProvider → SyncConnectionProvider → PrivateAppProviderInner
 *
 * Why outer/inner pattern:
 * - Outer provides FeaturebaseProvider and SyncConnectionProvider
 * - Inner consumes useSyncConnection() for reconnection modal trigger
 * - Prevents provider/consumer cycle anti-pattern
 */

import { useEffect, useRef, useState, useCallback, useMemo, lazy, Suspense, type ReactNode } from 'react';
import type { User } from '@/types/auth';
import type { ProviderKey } from '@/types/sync';
import { useToastNotification } from '@enfomentis/ui';
import { instrumentService } from '@/services/InstrumentService';
import { BackgroundSyncService } from '@/services/sync/BackgroundSyncService';
import { LocalDirectorySyncProvider } from '@/services/sync/LocalDirectorySyncProvider';
import { syncInitializationService } from '@/services/sync/SyncInitializationService';
import { SyncSettingsService } from '@/services/SyncSettingsService';
import { userSyncSettingsService } from '@/services/UserSyncSettingsService';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import { getProviderDisplayName } from '@/utils/syncProviderUtils';
import type { UserSyncSettings } from '@/types/supabase/UserSyncSettings';
import type { ConnectionResult } from '@/types/sync/SyncInitTypes';
import { ReconnectionModal } from '@/components/shared/sync/ReconnectionModal';
import { ReconnectionBanner } from '@/components/shared/sync/ReconnectionBanner';
import { SyncConnectionProvider, UserSyncSettingsContext } from '@/contexts';
import { useSyncConnection } from '@/contexts';
import { useSchemaVersionErrorToast } from '@/hooks';
import { FeaturebaseProvider } from '@/featurebase';

// DEV ONLY - Lazy import dev console (tree-shaken in production)
const DevConsole = import.meta.env.DEV
  ? lazy(() => import('@/dev').then(m => ({ default: m.DevConsole })))
  : null;

/**
 * Props for PrivateAppProvider
 */
export interface PrivateAppProviderProps {
  user: User;
  children: ReactNode;
}

/**
 * PrivateAppProvider (Outer) - Provides FeaturebaseProvider and SyncConnectionProvider
 *
 * IMPORTANT: Only mount when user is authenticated.
 * Mounted in AppWrapper with key={user.id} to force clean reset on account switching.
 */
export function PrivateAppProvider({ user, children }: PrivateAppProviderProps) {
  return (
    <FeaturebaseProvider user={user}>
      <SyncConnectionProvider userId={user.id}>
        <PrivateAppProviderInner user={user}>{children}</PrivateAppProviderInner>
      </SyncConnectionProvider>
    </FeaturebaseProvider>
  );
}

/**
 * PrivateAppProviderInner - Manages authenticated services and modals
 *
 * Uses useSyncConnection() to watch connection state and trigger reconnection modal.
 */
function PrivateAppProviderInner({ user, children }: PrivateAppProviderProps) {
  const { showToast } = useToastNotification();
  const { state: connectionState, hydrateFromInitResult, hydrateSuccess } = useSyncConnection();

  // Global toast handler for schema version errors (Phase 4B1)
  // Shows toast when SCHEMA_VERSION_NEWER detected (stale browser needs refresh)
  useSchemaVersionErrorToast();

  // Track previous settings for smart diff-based toasts
  const previousSettingsRef = useRef<UserSyncSettings | null>(null);

  // User sync settings state (exposed via context for AppSyncStatus and other consumers)
  const [userSyncSettings, setUserSyncSettings] = useState<UserSyncSettings | null>(null);
  const [userSyncSettingsLoading, setUserSyncSettingsLoading] = useState(true);

  // DEV ONLY - Dev console state
  const [showDevConsole, setShowDevConsole] = useState(false);

  // ReconnectionModal state (app-wide sync re-auth)
  const [reconnectionModal, setReconnectionModal] = useState<{
    isOpen: boolean;
    providerKey: ProviderKey | null;
  }>({
    isOpen: false,
    providerKey: null,
  });

  // Track if modal succeeded (prevent false "still disconnected" toast)
  const modalSucceededRef = useRef<boolean>(false);

  // Track if modal was dismissed (trigger banner display)
  const modalDismissedRef = useRef<boolean>(false);

  // Track settings loaded state (so modal effect can react to settings availability)
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // BackgroundSyncService idempotency guard (prevents StrictMode double-init)
  const syncServiceInitializedRef = useRef(false);

  // Deduplication ref for init success toast (prevents duplicate with realtime handler)
  const lastInitSuccessRef = useRef<{ providerKey: ProviderKey; timestamp: number } | null>(null);

  // Track offline failures for auto-retry when network is restored
  const failedDueToOfflineRef = useRef<boolean>(false);

  // Prevent overlapping retries on flappy networks
  const retryInFlightRef = useRef<boolean>(false);

  // Sync gate: block children render until initial sync completes (or timeout/skip)
  // Prevents stale IndexedDB data from rendering before cloud sync finishes
  const [syncReady, setSyncReady] = useState(false);
  const syncGateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncGateCompletedUnsubscribeRef = useRef<(() => void) | null>(null);
  const syncGateStatusUnsubscribeRef = useRef<(() => void) | null>(null);
  // Track if gate is waiting for reconnection (directory invalid, needs user to pick directory)
  const syncGateWaitingForReconnectionRef = useRef<boolean>(false);

  /**
   * Helper to open the sync gate (allow children to render)
   * Clears timeout, subscriptions, and waiting flag to prevent memory leaks and stale state
   */
  const openSyncGate = useCallback(() => {
    // Clear timeout if set
    if (syncGateTimeoutRef.current) {
      clearTimeout(syncGateTimeoutRef.current);
      syncGateTimeoutRef.current = null;
    }
    // Unsubscribe from completion listener
    if (syncGateCompletedUnsubscribeRef.current) {
      syncGateCompletedUnsubscribeRef.current();
      syncGateCompletedUnsubscribeRef.current = null;
    }
    // Unsubscribe from status listener
    if (syncGateStatusUnsubscribeRef.current) {
      syncGateStatusUnsubscribeRef.current();
      syncGateStatusUnsubscribeRef.current = null;
    }
    // Reset waiting flags (prevents stale state on subsequent syncs)
    syncGateWaitingForReconnectionRef.current = false;
    setSyncReady(true);
  }, []);

  /**
   * Handle sync initialization result - pure UX handling (toasts, modal, refs)
   * BackgroundSyncService wiring is handled separately in the caller
   */
  const handleInitResult = (result: ConnectionResult, isRetry?: boolean): void => {
    const logPrefix = isRetry ? '🔄 [Retry]' : '🔄 [Init]';

    // Success with provider: show toast and update refs
    if (result.success && result.provider) {
      const providerName = result.provider.name;
      const displayName = getProviderDisplayName(providerName);

      showToast({
        title: isRetry ? 'Sync Reconnected' : 'Sync Connected',
        description: `Connected to ${displayName}`,
        color: 'success',
        timeout: 3000
      });

      // Set deduplication ref (prevents duplicate toast from realtime handler)
      lastInitSuccessRef.current = { providerKey: providerName as ProviderKey, timestamp: Date.now() };

      // Clear offline failure flag
      failedDueToOfflineRef.current = false;

      console.log(`${logPrefix} ✅ Success with provider: ${displayName}`);
      return;
    }

    // Success without provider: no-op case (syncOnLogin=false or no activeSyncProvider)
    if (result.success && !result.provider) {
      failedDueToOfflineRef.current = false;
      console.log(`${logPrefix} ⏭️ Skipped (no provider configured or syncOnLogin disabled)`);
      return;
    }

    // Failure: hydrate shared connection state (single source of truth)
    // This allows ReconnectionModal/Banner to render correct content
    if (!result.success) {
      console.log(`${logPrefix} ⚠️ Failed:`, result.reason, result.error);

      // Clear deduplication ref on failure
      lastInitSuccessRef.current = null;

      // Hydrate shared connection state from init result
      // This will set state to needsReauth/offline/error/etc.
      // The existing effect watching connectionState.status === 'needsReauth' will open the modal
      // The banner logic will also react to the hydrated state
      hydrateFromInitResult(result);

      // Track offline state for auto-retry when network is restored
      if (result.reason === 'OFFLINE') {
        failedDueToOfflineRef.current = true;
        showToast({
          title: 'Sync Offline',
          description: 'Will connect when online',
          color: 'warning',
          timeout: 5000
        });
      } else if (!result.modalRequired) {
        // Non-modal failures: show toast (modal failures are handled by ReconnectionModal)
        failedDueToOfflineRef.current = false;
        showToast({
          title: 'Sync Connection Failed',
          description: result.error || 'Unable to connect. Please try again later.',
          color: 'danger',
          timeout: 5000
        });
      } else {
        // Modal-required failures: no toast needed, modal will show
        failedDueToOfflineRef.current = false;
      }
    }
  };

  /**
   * Check if the connected directory is still valid (exists on disk with correct ID)
   * Used by both init effect and online retry handler for Local Directory provider
   */
  const checkDirectoryValidity = async (
    syncService: BackgroundSyncService,
    provider: LocalDirectorySyncProvider
  ): Promise<void> => {
    try {
      // Load settings to check if we have a stored directory ID
      const settingsService = new SyncSettingsService();
      const settings = await settingsService.getOrCreateDefault();

      console.log('🔍 Checking directory validity...', {
        directoryId: settings.directoryId,
        syncEnabled: settings.syncEnabled
      });

      // Only check if we think we're connected and have a stored directory ID
      if (!settings.directoryId || !settings.syncEnabled) {
        console.log('⏭️  Skipping directory check (no directoryId or sync disabled)');
        return;
      }

      // Check if provider is still connected
      const connected = await provider.isConnected();
      console.log('📂 Directory connection status:', connected);

      if (!connected) {
        // Directory handle is invalid (likely deleted or moved)
        console.warn('⚠️ Directory is no longer accessible');

        // Auto-disconnect by stopping sync and clearing settings
        // Preserve activeSyncProvider so user can reconnect from Settings
        syncService.stop();
        await provider.disconnect();
        await settingsService.update(settings.id, {
          syncEnabled: false,
          directoryId: null
        });

        showToast({
          title: 'Directory No Longer Available',
          description: 'The sync directory was deleted or moved. Please reconnect to continue syncing.',
          color: 'warning',
          timeout: 8000
        });
        return;
      }

      // Verify the directory ID matches what we expect
      // This also tests if the directory is actually accessible (not just handle exists)
      const currentDirectoryId = await provider.getDirectoryId();

      if (!currentDirectoryId) {
        // Failed to access directory (returns null when directory was deleted)
        console.warn('⚠️ Directory no longer accessible (failed to read/create directory ID)');

        // Auto-disconnect - preserve activeSyncProvider so user can reconnect from Settings
        syncService.stop();
        await provider.disconnect();
        await settingsService.update(settings.id, {
          syncEnabled: false,
          directoryId: null
        });

        showToast({
          title: 'Directory No Longer Available',
          description: 'The sync directory was deleted or moved. Please reconnect to continue syncing.',
          color: 'warning',
          timeout: 8000
        });
        return;
      }

      if (currentDirectoryId !== settings.directoryId) {
        // Directory ID mismatch - directory was replaced or .app-directory-id was deleted
        console.warn('⚠️ Directory ID mismatch - directory may have been replaced');

        // Auto-disconnect - preserve activeSyncProvider so user can reconnect from Settings
        syncService.stop();
        await provider.disconnect();
        await settingsService.update(settings.id, {
          syncEnabled: false,
          directoryId: null
        });

        showToast({
          title: 'Directory Changed',
          description: 'The sync directory appears to have changed. Please reconnect to continue syncing.',
          color: 'warning',
          timeout: 8000
        });
      }
    } catch (error) {
      console.error('Failed to validate directory:', error);
      // Don't show error toast - this is a background check
    }
  };

  // Initialize sync via SyncInitializationService when user logs in
  useEffect(() => {
    // DEV ONLY: Skip init for E2E tests that need to control state via URL params
    // This allows tests to use errorState/mockLocalDirectorySuccess without init intercepting
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('skipInit') === 'true') {
        console.log('[PrivateAppProvider] Skipping sync init (skipInit=true)');
        openSyncGate(); // Open gate immediately for tests
        return;
      }
    }

    const controller = new AbortController();

    const initializeSyncOnLogin = async (): Promise<void> => {
      try {
        console.log('🔄 Initializing sync via SyncInitializationService...');

        // Call the service with abort signal
        const result = await syncInitializationService.initializeOnLogin(user.id, {
          signal: controller.signal
        });

        // Early return on abort (check signal after await)
        if (controller.signal.aborted) {
          console.log('⏹️ Sync initialization aborted (component unmounted)');
          return;
        }

        // Handle ABORTED reason as silent no-op (no modal/toast)
        if (!result.success && result.reason === 'ABORTED') {
          console.log('⏹️ Sync initialization was aborted');
          openSyncGate(); // Open gate on abort (fallback to local data)
          return;
        }

        // Success with provider: wire BackgroundSyncService, then handle UX
        if (result.success && result.provider) {
          console.log('✅ Sync provider connected, initializing BackgroundSyncService...');

          // Get or create stable device ID
          const deviceId = getOrCreateDeviceId();

          // Initialize BackgroundSyncService with idempotency guard
          const syncService = BackgroundSyncService.getInstance();
          if (!syncServiceInitializedRef.current) {
            await syncService.initialize(user.id, deviceId);
            syncServiceInitializedRef.current = true;
          } else {
            console.log('⏭️ BackgroundSyncService already initialized (StrictMode double-run)');
          }

          // Set the provider from service result
          syncService.setProvider(result.provider);

          // Start the service (it will check if sync is enabled in settings)
          const startSuccess = await syncService.start();

          console.log('✅ BackgroundSyncService initialized and started');

          // Set up sync gate: wait for first sync to complete before rendering children
          if (startSuccess) {
            console.log('🚦 [SyncGate] Service started, triggering immediate sync...');

            // Subscribe to sync completion - opens gate when first sync finishes
            syncGateCompletedUnsubscribeRef.current = syncService.onSyncCompleted(() => {
              console.log('🚦 [SyncGate] First sync completed, opening gate');
              openSyncGate();
            });

            // Subscribe to status changes - opens gate on error (don't hang on failed sync)
            syncGateStatusUnsubscribeRef.current = syncService.onStatusChanged((status) => {
              if (status === 'error') {
                console.log('🚦 [SyncGate] Sync error detected, opening gate (fallback to local)');
                openSyncGate();
              }
            });

            // Timeout fallback: don't block UI forever if sync hangs
            syncGateTimeoutRef.current = setTimeout(() => {
              console.log('🚦 [SyncGate] Timeout (5s), opening gate (fallback to local)');
              openSyncGate();
            }, 5000);

            // Trigger immediate sync
            console.log('🚦 [SyncGate] Calling syncNow() for immediate sync...');
            void syncService.syncNow();
          } else {
            // Service didn't start (sync disabled, no provider, etc.) - open gate immediately
            console.log('🚦 [SyncGate] Service did not start, opening gate immediately');
            openSyncGate();
          }

          // Check directory validity ONLY for Local Directory provider (type narrowing)
          if (result.provider instanceof LocalDirectorySyncProvider) {
            await checkDirectoryValidity(syncService, result.provider);
          }

          // Hydrate shared connection state to success
          // This ensures SyncConnectionContext reflects the initialization outcome
          hydrateSuccess(result.provider.name as ProviderKey);

          // Handle UX (toast, refs)
          handleInitResult(result);
          return;
        }

        // Non-success cases: delegate to shared UX handler
        handleInitResult(result);

        // Special case: DIRECTORY_INVALID needs user to pick directory
        // Keep gate closed so reconnection modal shows over the sync gate (not dashboard)
        if (result.reason === 'DIRECTORY_INVALID') {
          console.log('🚦 [SyncGate] Directory invalid, waiting for reconnection before opening gate');
          syncGateWaitingForReconnectionRef.current = true;
          // Don't open gate - reconnection modal will open and user must pick directory first
          return;
        }

        // Open gate for other non-success cases (no provider configured, or other failures)
        console.log('🚦 [SyncGate] Non-success init result, opening gate (fallback to local)');
        openSyncGate();
      } catch (error) {
        // Check if aborted before logging error
        if (controller.signal.aborted) {
          console.log('⏹️ Sync initialization aborted during execution');
          return;
        }
        console.error('❌ Failed to initialize sync:', error);
        // Don't show error toast on startup - this is background functionality
        // Open gate on exception (fallback to local data)
        console.log('🚦 [SyncGate] Init exception, opening gate (fallback to local)');
        openSyncGate();
      }
    };

    void initializeSyncOnLogin();

    // Cleanup: abort on unmount and clear refs
    return () => {
      controller.abort();
      // Clear refs to prevent stale state on logout/remount
      lastInitSuccessRef.current = null;
      failedDueToOfflineRef.current = false;
      // Clean up sync gate subscriptions and timeout
      if (syncGateTimeoutRef.current) {
        clearTimeout(syncGateTimeoutRef.current);
        syncGateTimeoutRef.current = null;
      }
      if (syncGateCompletedUnsubscribeRef.current) {
        syncGateCompletedUnsubscribeRef.current();
        syncGateCompletedUnsubscribeRef.current = null;
      }
      if (syncGateStatusUnsubscribeRef.current) {
        syncGateStatusUnsubscribeRef.current();
        syncGateStatusUnsubscribeRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, openSyncGate]);
  // Note: showToast and handleInitResult are intentionally excluded from deps

  // Auto-retry initialization when network is restored after OFFLINE failure
  useEffect(() => {
    const handleOnline = async (): Promise<void> => {
      // Only retry if we previously failed due to offline
      if (!failedDueToOfflineRef.current) {
        console.log('🌐 Network online, but no offline failure to retry');
        return;
      }

      // Prevent overlapping retries on flappy networks
      if (retryInFlightRef.current) {
        console.log('🌐 Network online, but retry already in progress');
        return;
      }

      retryInFlightRef.current = true;
      failedDueToOfflineRef.current = false;

      console.log('🌐 Network restored, retrying sync initialization...');

      try {
        const result = await syncInitializationService.initializeOnLogin(user.id);

        // Success with provider: wire BackgroundSyncService, then handle UX
        if (result.success && result.provider) {
          console.log('🌐 [Retry] Sync provider connected, initializing BackgroundSyncService...');

          const deviceId = getOrCreateDeviceId();
          const syncService = BackgroundSyncService.getInstance();

          if (!syncServiceInitializedRef.current) {
            await syncService.initialize(user.id, deviceId);
            syncServiceInitializedRef.current = true;
          }

          syncService.setProvider(result.provider);
          await syncService.start();

          console.log('🌐 [Retry] BackgroundSyncService initialized and started');

          // Check directory validity for Local Directory provider
          if (result.provider instanceof LocalDirectorySyncProvider) {
            await checkDirectoryValidity(syncService, result.provider);
          }

          // Hydrate shared connection state to success
          hydrateSuccess(result.provider.name as ProviderKey);
        }

        // Handle UX (toast, modal, refs)
        handleInitResult(result, true);
      } catch (error) {
        console.error('🌐 [Retry] Failed to initialize sync:', error);
      } finally {
        retryInFlightRef.current = false;
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
      retryInFlightRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);
  // Note: showToast and handleInitResult are intentionally excluded from deps

  // Check for actionable catalog updates on app startup
  useEffect(() => {
    const checkActionableUpdates = async () => {
      try {
        // Get user's last known version from localStorage
        const lastKnownVersion = localStorage.getItem('instrument_catalog_version');

        // Check for catalog updates
        const updateInfo = await instrumentService.checkInstrumentCatalogUpdates(lastKnownVersion || undefined);

        if (updateInfo.hasUpdates) {
          // Check if user has instruments that are actually outdated
          const userInstruments = await instrumentService.getAll();
          const templatedInstruments = userInstruments.filter(inst => inst.instrumentReferenceId);

          let outdatedCount = 0;
          const outdatedNames: string[] = [];

          for (const instrument of templatedInstruments) {
            const status = instrumentService.getTemplateStatus(instrument);
            if (status === 'outdated') {
              outdatedCount++;
              if (outdatedNames.length < 3) { // Show max 3 names
                outdatedNames.push(instrument.symbol);
              }
            }
          }

          if (outdatedCount > 0) {
            const namesList = outdatedNames.length < outdatedCount
              ? `${outdatedNames.join(', ')} and ${outdatedCount - outdatedNames.length} more`
              : outdatedNames.join(', ');

            showToast({
              title: 'Template Updates Available',
              description: `${outdatedCount} of your instruments (${namesList}) have updates. Check Instrument Explorer to review.`,
              color: 'warning',
              timeout: 10000,
            });
          }

          // Update user's last known version in localStorage
          localStorage.setItem('instrument_catalog_version', updateInfo.currentVersion);
        }
      } catch (error) {
        console.error('Failed to check actionable updates on startup:', error);
        // Don't show error toast on startup - this is background functionality
      }
    };

    checkActionableUpdates();
  }, [showToast]);

  // Global realtime subscription: Settings changes
  // Settings are operationally-critical - affect background sync, session close
  // Subscribe globally so changes are detected regardless of current page
  useEffect(() => {
    console.log('🔔 [PrivateAppProvider] Subscribing to settings changes globally');
    const unsubscribe = userSyncSettingsService.subscribeToSettingsChanges(
      user.id,
      (updated) => {
        console.log('🔄 [PrivateAppProvider] Settings updated from another device:', updated);

        // Smart toast logic
        const result = userSyncSettingsService.handleSettingsUpdate(
          previousSettingsRef.current,
          updated
        );

        if (result.showToast && result.toastMessage) {
          showToast({
            title: 'Sync Settings Changed',
            description: result.toastMessage,
            color: 'default'
          });
        }

        // Notify BackgroundSyncService if needed (service tells us via shouldAffectBackgroundSync)
        if (result.shouldAffectBackgroundSync) {
          // TODO: Notify BackgroundSyncService to adjust timers
          // BackgroundSyncService.getInstance().updateSettings(updated);
          console.log('⏱️ BackgroundSyncService should update with new settings:', {
            syncEnabled: updated.syncEnabled,
            syncInterval: updated.syncInterval
          });
        }

        // Update ref for next comparison and state for context consumers
        previousSettingsRef.current = updated;
        setUserSyncSettings(updated);
      }
    );

    return unsubscribe;
  }, [user.id, showToast]);

  // Initialize previous settings ref for diff-based toasts and expose via context
  useEffect(() => {
    // Reset state before fetching new user's settings
    // This prevents stale settings from previous user during user switch/sign-out
    previousSettingsRef.current = null;
    setUserSyncSettings(null);
    setUserSyncSettingsLoading(true);
    setSettingsLoaded(false);

    const loadInitialSettings = async () => {
      try {
        const settings = await userSyncSettingsService.getOrCreateSettings(user.id);
        previousSettingsRef.current = settings;
        setUserSyncSettings(settings); // Update context state
        setUserSyncSettingsLoading(false); // Mark loading complete
        setSettingsLoaded(true); // Signal that settings are now available
        console.log('📋 [PrivateAppProvider] Loaded initial settings for diff tracking');
      } catch (error) {
        console.error('Failed to load initial settings:', error);
        setUserSyncSettingsLoading(false); // Mark loading complete even on error
      }
    };

    loadInitialSettings();
  }, [user.id]);

  // Effect: Show ReconnectionModal for needsReauth (app-wide)
  useEffect(() => {
    // Early return: not in needsReauth state
    if (connectionState.status !== 'needsReauth') return;

    // Re-entrancy guard: prevent re-opening if modal already open
    if (reconnectionModal.isOpen) return;

    // Success guard: prevent reopening if connection just succeeded
    // Modal's auto-close timer causes reconnectionModal.isOpen to change,
    // which would re-trigger this effect while connectionState hasn't updated yet
    if (modalSucceededRef.current) {
      console.log('[PrivateAppProvider] Skipping modal reopen - connection just succeeded');
      return;
    }

    // Dismissal guard: prevent reopening if user dismissed modal
    // Show global reconnection banner instead (rendered below in this provider)
    if (modalDismissedRef.current) {
      console.log('[PrivateAppProvider] Skipping modal reopen - user dismissed, banner will show');
      return;
    }

    // Wait for userSettings to load before determining provider
    // Reuse existing previousSettingsRef (already loaded by lines 325-337)
    if (!previousSettingsRef.current) {
      console.log('[PrivateAppProvider] Settings not loaded yet, waiting...');
      return;
    }

    // Get actual provider from userSettings (works for both Google Drive and Local Directory)
    // Both providers can trigger needsReauth state:
    // - Google Drive: TOKEN_EXPIRED, TOKEN_REVOKED
    // - Local Directory: DIRECTORY_INVALID, PERMISSION_DENIED
    const providerKey: ProviderKey = (previousSettingsRef.current.activeSyncProvider as ProviderKey) || 'google-drive';

    console.log('[PrivateAppProvider] Opening reconnection modal for provider:', providerKey);

    // Reset success tracking ref when opening modal
    modalSucceededRef.current = false;

    setReconnectionModal({
      isOpen: true,
      providerKey,
    });
  }, [connectionState.status, reconnectionModal.isOpen, settingsLoaded]);

  // Global banner visibility logic
  // Banner shows for dismissed needsReauth or transient errors
  const shouldShowBanner = useCallback(() => {
    // Guard 1: Modal takes precedence (don't show if modal open)
    if (reconnectionModal.isOpen) return false;

    // Guard 2: Must have active provider (same as modal pattern)
    const providerKey = previousSettingsRef.current?.activeSyncProvider;
    if (!providerKey) return false;

    // Guard 3a: Show for dismissed needsReauth states (Bug 4 requirement)
    if (connectionState.status === 'needsReauth' && modalDismissedRef.current) {
      return true;
    }

    // Guard 3b: Show for banner-appropriate transient errors
    // These are errors that don't require modal (offline, rate limited, generic errors)
    const MODAL_REQUIRED_REASONS = [
      'TOKEN_EXPIRED',
      'TOKEN_REVOKED',
      'PERMISSION_DENIED',
      'DIRECTORY_INVALID'
    ];

    const isBannerState =
      connectionState.status === 'offline' ||
      connectionState.status === 'rateLimited' ||
      connectionState.status === 'connecting' ||        // Keep banner mounted during reconnection
      connectionState.status === 'remoteConnecting' ||  // Keep banner mounted during cross-tab passive state
      connectionState.status === 'success' ||           // Keep banner mounted to show success animation (3s auto-close)
      (connectionState.status === 'error' &&
        connectionState.reason &&
        !MODAL_REQUIRED_REASONS.includes(connectionState.reason));

    return isBannerState;
  }, [connectionState, reconnectionModal.isOpen]);

  // Global realtime subscription: Connection events
  // Connection status is security-critical - user needs to know if backup stopped
  // Subscribe globally to show toast warnings regardless of current page
  useEffect(() => {
    console.log('🔔 [PrivateAppProvider] Subscribing to connection events globally');
    const unsubscribe = userSyncSettingsService.subscribeToConnectionEvents(
      user.id,
      async (result) => {
        console.log('🔄 [PrivateAppProvider] Connection event:', result);

        // Deduplication check for "Sync Reconnected" toasts
        // Prevents duplicate toast when init success and realtime event fire close together
        if (result.isConnected && lastInitSuccessRef.current) {
          // Get provider key from settings ref (realtime payload doesn't include provider)
          const currentProviderKey = previousSettingsRef.current?.activeSyncProvider as ProviderKey | undefined;

          // Check if this is a duplicate of recent init success
          if (currentProviderKey &&
              lastInitSuccessRef.current.providerKey === currentProviderKey &&
              Date.now() - lastInitSuccessRef.current.timestamp < 5000) {
            console.log('⏭️ [PrivateAppProvider] Skipping duplicate realtime toast (init just succeeded)');
            return;
          }
        }

        // Show toast for both connect and disconnect (security events)
        if (result.showToast && result.toastMessage) {
          showToast({
            title: result.isConnected ? 'Sync Reconnected' : 'Sync Disconnected',
            description: result.toastMessage,
            color: result.isConnected ? 'success' : 'warning'
          });
        }
      }
    );

    return unsubscribe;
  }, [user.id, showToast]);

  // DEV ONLY - Check query param and sessionStorage for dev console
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    // Check query param
    const params = new URLSearchParams(window.location.search);
    if (params.get('dev') === '1') {
      setShowDevConsole(true);
    }

    // Check sessionStorage
    const stored = sessionStorage.getItem('dev-console-open');
    if (stored === 'true') {
      setShowDevConsole(true);
    }
  }, []);

  // DEV ONLY - Persist dev console state to sessionStorage
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    sessionStorage.setItem('dev-console-open', showDevConsole ? 'true' : 'false');
  }, [showDevConsole]);

  // DEV ONLY - Keyboard shortcut for dev console
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const handleKeydown = (e: KeyboardEvent): void => {
      // Cmd/Ctrl + Shift + K
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'K') {
        e.preventDefault();
        setShowDevConsole(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, []);

  // DEV ONLY - Expose testing utilities globally
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    // Dynamically import dev services to expose globally
    void (async () => {
      const { backgroundSyncServiceDev } = await import('@/services/sync/BackgroundSyncService');

      // Expose globally for console scripting (merge with existing appTest)
      window.appTest = window.appTest || {};
      Object.assign(window.appTest, {
        sync: backgroundSyncServiceDev,
      });

      console.log('[DEV] 🧪 Test utilities exposed as window.appTest');
    })();
  }, []);

  /**
   * Handle successful reconnection
   */
  const handleReconnectionSuccess = useCallback((providerKey: ProviderKey) => {
    console.log('[PrivateAppProvider] Reconnection succeeded for provider:', providerKey);

    // Mark success so onOpenChange won't show "still disconnected" toast
    modalSucceededRef.current = true;

    // Clear dismissal flag since connection succeeded
    modalDismissedRef.current = false;

    // If sync gate was waiting for reconnection, trigger sync and open gate
    if (syncGateWaitingForReconnectionRef.current) {
      console.log('🚦 [SyncGate] Reconnection succeeded, triggering sync then opening gate');
      const syncService = BackgroundSyncService.getInstance();

      // Check if service is running (it may have been started by the reconnection flow)
      if (syncService.getIsRunning()) {
        // Subscribe to completion before triggering sync
        syncGateCompletedUnsubscribeRef.current = syncService.onSyncCompleted(() => {
          console.log('🚦 [SyncGate] Post-reconnection sync completed, opening gate');
          openSyncGate();
        });
        // Set timeout for this sync
        syncGateTimeoutRef.current = setTimeout(() => {
          console.log('🚦 [SyncGate] Post-reconnection sync timeout (5s), opening gate');
          openSyncGate();
        }, 5000);
        void syncService.syncNow();
      } else {
        // Service not running yet - just open the gate, next sync will happen on interval
        console.log('🚦 [SyncGate] Service not running yet, opening gate');
        openSyncGate();
      }
    }

    // Modal will auto-close via its internal timer (3s)
    // No need to manually close - let the timer handle it
  }, [openSyncGate]);

  /**
   * Handle modal cancellation (user dismissed without reconnecting)
   */
  const handleReconnectionCancel = useCallback(() => {
    console.log('[PrivateAppProvider] Reconnection cancelled by user');

    // Mark modal as dismissed to show banner instead
    modalDismissedRef.current = true;

    // If sync gate was waiting for reconnection, open gate anyway (user's choice to dismiss)
    // They can reconnect later via the banner
    if (syncGateWaitingForReconnectionRef.current) {
      console.log('🚦 [SyncGate] User dismissed reconnection modal, opening gate (fallback to local)');
      openSyncGate();
    }

    // Modal closes, but we don't mark success
    // Success ref stays false, so dismissal tracking works correctly
  }, [openSyncGate]);

  /**
   * Handle successful reconnection from global banner
   */
  const handleGlobalBannerSuccess = useCallback(() => {
    console.log('[PrivateAppProvider] Banner reconnection succeeded');

    // Clear dismissal flag since connection succeeded
    modalDismissedRef.current = false;

    // Banner will auto-hide via its internal 3s success timeout
    // shouldShowBanner keeps it mounted during 'success' state to show animation
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const userSyncSettingsContextValue = useMemo(
    () => ({
      settings: userSyncSettings,
      isLoading: userSyncSettingsLoading,
    }),
    [userSyncSettings, userSyncSettingsLoading]
  );

  return (
    <UserSyncSettingsContext.Provider value={userSyncSettingsContextValue}>
      {/* Sync gate: block children until initial sync completes */}
      {syncReady ? (
        children
      ) : (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-default-500">Syncing your data...</p>
          </div>
        </div>
      )}

      {/* Global reconnection modal (app-wide sync re-auth) */}
      {reconnectionModal.isOpen && reconnectionModal.providerKey && (
        <ReconnectionModal
          isOpen={reconnectionModal.isOpen}
          onOpenChange={(open) => {
            // Update modal state using functional updater (avoids stale closure)
            setReconnectionModal(prev => ({
              isOpen: open,
              providerKey: open ? prev.providerKey : null,
            }));
          }}
          providerKey={reconnectionModal.providerKey}
          onSuccess={handleReconnectionSuccess}
          onCancel={handleReconnectionCancel}
        />
      )}

      {/* Global reconnection banner for transient errors and dismissed modals */}
      {shouldShowBanner() && previousSettingsRef.current?.activeSyncProvider && user?.id && (
        <ReconnectionBanner
          userId={user.id}
          providerKey={previousSettingsRef.current.activeSyncProvider as ProviderKey}
          onSuccess={handleGlobalBannerSuccess}
        />
      )}

      {/* DEV ONLY - Test console */}
      {import.meta.env.DEV && DevConsole && (
        <Suspense fallback={null}>
          <DevConsole
            isOpen={showDevConsole}
            onToggle={() => setShowDevConsole(prev => !prev)}
          />
        </Suspense>
      )}
    </UserSyncSettingsContext.Provider>
  );
}
